import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Globe, FileText, CreditCard, RotateCcw, UserCheck } from "lucide-react";

const ICONS = {
  globe: Globe,
  "file-text": FileText,
  "credit-card": CreditCard,
  "rotate-ccw": RotateCcw,
  "user-check": UserCheck,
  "shield-check": ShieldCheck,
};

export const CUSTOMER_PORTAL_DEFAULT_DATA = {
  title_lead: "Customer",
  title_accent: "Portal",
  tagline: "Give your customers a modern self-service portal for order tracking, proof approvals, payments, and account management - all in one place.",
  features: [
    { icon: "globe",        title: "Order Tracking",     desc: "Customers can view real-time order status updates from anywhere." },
    { icon: "file-text",    title: "Proof Approvals",    desc: "Review and approve art proofs directly from the portal." },
    { icon: "credit-card",  title: "Online Payments",    desc: "Pay invoices and make deposits securely online." },
    { icon: "rotate-ccw",   title: "Easy Reorders",      desc: "Quickly reorder from past order history with saved preferences." },
    { icon: "user-check",   title: "Account Management", desc: "Update contact info, manage users, and view statements." },
    { icon: "shield-check", title: "Secure Access",      desc: "Role-based permissions with enterprise-grade security." },
  ],
  cta_label: "Request a Demo",
  cta_href: "/contact",
};

export default function CustomerPortalPage({ data }) {
  const src = data || {};
  const d = { ...CUSTOMER_PORTAL_DEFAULT_DATA, ...src };
  const features = Array.isArray(src.features) && src.features.length > 0
    ? src.features
    : CUSTOMER_PORTAL_DEFAULT_DATA.features;

  return (
    <div data-testid="customer-portal-page" className="page-transition">
      <section className="pt-32 pb-14 lg:pt-40 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
              {d.title_lead} <span className="text-sky-600">{d.title_accent}</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
              {d.tagline}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = ICONS[f.icon] || Globe;
              return (
                <motion.div
                  key={f.title || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
          {d.cta_label ? (
            <div className="text-center mt-12">
              <Link to={d.cta_href || "/contact"}>
                <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-12 text-sm font-semibold">
                  {d.cta_label} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
